import { useState } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Card, IconButton, LoadingSpinner } from '../ui';
import FilterableTable from '../FilterableTable';
import { useDatabases, useDeleteDatabase } from '../../hooks/useDatabases';

export default function DatabaseList({ onSelect, onCreateNew }) {
  const { data: databases = [], isLoading } = useDatabases();
  const deleteDatabase = useDeleteDatabase();
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const handleDelete = (e, db) => {
    e.stopPropagation();
    if (confirmDeleteId === db.id) {
      deleteDatabase.mutate(db.id, {
        onSettled: () => setConfirmDeleteId(null),
      });
    } else {
      setConfirmDeleteId(db.id);
    }
  };

  const handleCancelDelete = (e) => {
    e.stopPropagation();
    setConfirmDeleteId(null);
  };

  if (isLoading) {
    return (
      <Card>
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      </Card>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-text-primary-light dark:text-text-primary">
          Databases
        </h2>
        <IconButton
          icon={PlusIcon}
          label="New Database"
          variant="primary"
          size="sm"
          display="both"
          onClick={onCreateNew}
        />
      </div>

      {databases.length === 0 ? (
        <Card>
          <div className="text-center py-8 text-text-muted-light dark:text-text-muted text-sm">
            No databases found
          </div>
        </Card>
      ) : (
        <Card padding="none">
          <FilterableTable
            data={databases.map(db => ({
              ...db,
              onClick: () => onSelect(db),
            }))}
            searchFields={['name', 'slug']}
            searchPlaceholder="Search databases..."
            showFilters={true}
            showPagination={databases.length > 10}
            defaultItemsPerPage={10}
            defaultSortColumn="name"
            defaultSortDirection="asc"
            cellPadding="compact"
            columns={[
              {
                key: 'name',
                header: 'Name',
                align: 'left',
                className: 'font-semibold text-text-primary-light dark:text-text-primary cursor-pointer hover:opacity-70 transition',
                render: (db) => db.name,
              },
              {
                key: 'slug',
                header: 'Slug',
                align: 'left',
                className: 'text-text-muted-light dark:text-text-muted',
                render: (db) => db.slug,
              },
              {
                key: 'columns',
                header: 'Columns',
                align: 'right',
                className: 'text-text-secondary-light dark:text-text-secondary',
                render: (db) => db.columns?.length ?? db.columnCount ?? '\u2014',
                sortValue: (db) => db.columns?.length ?? db.columnCount ?? 0,
              },
              {
                key: 'metrics',
                header: 'Metrics',
                align: 'right',
                className: 'text-text-secondary-light dark:text-text-secondary',
                render: (db) => db.metrics?.length ?? db.metricCount ?? '\u2014',
                sortValue: (db) => db.metrics?.length ?? db.metricCount ?? 0,
              },
              {
                key: 'responses',
                header: 'Responses',
                align: 'right',
                className: 'text-text-secondary-light dark:text-text-secondary',
                render: (db) => db.stats?.responseCount || 0,
                sortValue: (db) => db.stats?.responseCount || 0,
              },
              {
                key: 'companies',
                header: 'Companies',
                align: 'right',
                className: 'text-text-secondary-light dark:text-text-secondary',
                render: (db) => db.stats?.companyCount || 0,
                sortValue: (db) => db.stats?.companyCount || 0,
              },
              {
                key: 'isDefault',
                header: 'Default',
                align: 'center',
                render: (db) =>
                  db.isDefault ? (
                    <span className="text-[11px] px-1.5 py-0 rounded font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      Default
                    </span>
                  ) : null,
                sortValue: (db) => (db.isDefault ? 1 : 0),
              },
              {
                key: 'actions',
                header: '',
                align: 'right',
                render: (db) =>
                  confirmDeleteId === db.id ? (
                    <span className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                      <span className="text-xs text-text-muted-light dark:text-text-muted">Delete?</span>
                      <button
                        onClick={(e) => handleDelete(e, db)}
                        className="text-xs font-medium text-red-600 dark:text-red-400 hover:underline cursor-pointer transition-colors"
                      >
                        Yes
                      </button>
                      <button
                        onClick={handleCancelDelete}
                        className="text-xs font-medium text-text-secondary-light dark:text-text-secondary hover:underline cursor-pointer transition-colors"
                      >
                        No
                      </button>
                    </span>
                  ) : (
                    <IconButton
                      icon={TrashIcon}
                      label="Delete"
                      variant="danger"
                      size="xs"
                      display="icon"
                      onClick={(e) => handleDelete(e, db)}
                    />
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
